import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FaArrowLeft } from 'react-icons/fa';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useDropzone } from 'react-dropzone';
import { FaTimes } from 'react-icons/fa';
const BQ_BASE_URL = process.env.REACT_APP_BQ_BASE_URL;

function ExistingRoom() {
  const [roomType, setRoomType] = useState('');
  const [roomTypeId, setRoomTypeId] = useState(null);
  const [description, setDescription] = useState('');
  const [basePrice, setBasePrice] = useState('');
  const [maxOccupancy, setMaxOccupancy] = useState('');
  const [depositAmount, setDepositAmount] = useState('');
  const [isRefundable, setIsRefundable] = useState(true);
  const [images, setImages] = useState([]);
  const [selectedAmenities, setSelectedAmenities] = useState([]);
  const [selectedServiceCategories, setSelectedServiceCategories] = useState([]);
  const [roomTypes, setRoomTypes] = useState([]);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [roomToDelete, setRoomToDelete] = useState(null);

  // Loading states
  const [isLoading, setIsLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Handle drag-and-drop functionality
  const onDrop = (acceptedFiles) => {
    const newImages = acceptedFiles.map((file) =>
      Object.assign(file, {
        preview: URL.createObjectURL(file),
      })
    );
    setImages([...images, ...newImages]);
  };

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    accept: 'image/*',
    multiple: true,
  });

  const handleCheckboxChange = (list, setList, item) => {
    setList((prev) => (prev.includes(item) ? prev.filter((i) => i !== item) : [...prev, item]));
  };

  const removeImage = (indexToRemove) => {
    setImages((prevImages) => prevImages.filter((_, index) => index !== indexToRemove));
  };

  const toggleRefundable = () => {
    setIsRefundable(!isRefundable);
  };

  const handleSubmit = (e, action) => {
    e.preventDefault();

    if (action === 'delete') {
      const selectedRoom = roomTypes.find((room) => room.roomtypename === roomType);
      if (selectedRoom) {
        setRoomToDelete(selectedRoom);
        setIsDeleteModalOpen(true);
      } else {
        toast.error('Selected room type not found.');
      }
    } else if (action === 'update') {
      updateRoomType();
    }
  };

  const deleteRoomType = () => {
    if (!roomToDelete) {
      toast.error('No room selected for deletion.');
      return;
    }

    setIsDeleting(true);
    fetch(`${BQ_BASE_URL}/bq/api/roomtypes/${roomToDelete.roomtypeid}`, {
      method: 'DELETE',
      headers: {
        Accept: 'application/json',
      },
    })
      .then((response) => {
        if (response.ok) {
          toast.success('Room type deleted successfully!');
          resetForm();
          setIsDeleteModalOpen(false);
          // Refresh room types after deletion
          fetchRoomTypes();
        } else {
          toast.error('Failed to delete room type.');
        }
      })
      .catch((error) => {
        console.error('Error deleting room type:', error);
        toast.error('Failed to delete room type.');
      })
      .finally(() => {
        setIsDeleting(false);
      });
  };

  const updateRoomType = async () => {
    if (!roomTypeId) {
      toast.error('Please select a room type to update.');
      return;
    }

    setIsUpdating(true);
    const formData = new FormData();

    // Create the roomtype_data object and stringify it
    const roomtypeData = {
      propertyid: 1, // You'll need to get this from somewhere
      roomtypename: roomType,
      description: description,
      baseprice: basePrice,
      max_occupancy: maxOccupancy,
      deposit_amount: depositAmount,
      is_refundable: isRefundable,
      amenity_category_ids: selectedAmenities,
      roomservice_category_ids: selectedServiceCategories,
    };

    formData.append('roomtype_data', JSON.stringify(roomtypeData));

    // Add images
    images.forEach((img) => {
      if (img instanceof File) {
        formData.append('images', img);
      }
    });

    try {
      const response = await fetch(`${BQ_BASE_URL}/bq/api/editadmin-roomtypes/${roomTypeId}`, {
        method: 'PUT',
        body: formData,
      });

      if (response.ok) {
        toast.success('Room type updated successfully!');
        setTimeout(() => {
          window.location.reload();
        }, 2000);
      } else {
        const errorData = await response.json();
        toast.error(errorData.detail || 'Failed to update room type.');
      }
    } catch (error) {
      console.error('Update error:', error);
      toast.error('Error updating room type.');
    } finally {
      setIsUpdating(false);
    }
  };

  const resetForm = () => {
    setRoomType('');
    setRoomTypeId(null);
    setDescription('');
    setBasePrice('');
    setMaxOccupancy('');
    setDepositAmount('');
    setIsRefundable(true);
    setImages([]);
    setSelectedAmenities([]);
    setSelectedServiceCategories([]);
  };

  const fetchRoomTypes = () => {
    setIsFetching(true);
    fetch(`${BQ_BASE_URL}/bq/api/admin-roomtypes/`)
      .then((response) => {
        if (!response.ok) {
          throw new Error('Network response was not ok');
        }
        return response.json();
      })
      .then((data) => {
        if (!data.roomtypes || !Array.isArray(data.roomtypes)) {
          console.error('Expected roomtypes array but got:', data);
          setRoomTypes([]);
        } else {
          setRoomTypes(data.roomtypes);
          // You might want to store all_amenities and all_roomservices too
        }
      })
      .catch((error) => {
        console.error('Error fetching room types:', error);
        toast.error('Failed to load room types');
        setRoomTypes([]);
      })
      .finally(() => {
        setIsFetching(false);
      });
  };

  useEffect(() => {
    fetchRoomTypes();

    // Cleanup function to revoke object URLs
    return () => {
      images.forEach((image) => {
        if (image.preview && image.preview.startsWith('blob:')) {
          URL.revokeObjectURL(image.preview);
        }
      });
    };
  }, []);

  useEffect(() => {
    if (roomType) {
      const selectedRoom = roomTypes.find((room) => room.roomtypename === roomType);

      if (selectedRoom) {
        setRoomTypeId(selectedRoom.roomtypeid);
        setDescription(selectedRoom.description);
        setBasePrice(selectedRoom.baseprice);
        setMaxOccupancy(selectedRoom.max_occupancy);
        setDepositAmount(selectedRoom.deposit_amount);
        setIsRefundable(selectedRoom.is_refundable);
        setSelectedAmenities(selectedRoom.amenities.map((amenity) => amenity.id));
        setSelectedServiceCategories(
          selectedRoom.service_categories?.map((service) => service.id) || []
        );

        // Clean up old preview URLs
        images.forEach((image) => {
          if (image.preview && image.preview.startsWith('blob:')) {
            URL.revokeObjectURL(image.preview);
          }
        });

        // Set new images with proper preview URLs
        setImages(
          selectedRoom.image_urls.map((url) => ({
            name: url,
            preview: url,
            isExisting: true,
          }))
        );
      }
    }
  }, [roomType, roomTypes]);

  // Extract unique amenities and service categories from all room types
  const allAmenities = roomTypes.flatMap((roomType) => roomType.amenities || []);
  const uniqueAmenities = Array.from(new Set(allAmenities.map((a) => a.id))).map((id) =>
    allAmenities.find((a) => a.id === id)
  );

  const allServiceCategories = roomTypes.flatMap((roomType) => roomType.service_categories || []);
  const uniqueServiceCategories = Array.from(new Set(allServiceCategories.map((s) => s.id))).map(
    (id) => allServiceCategories.find((s) => s.id === id)
  );

  return (
    <div className="p-6 bg-gray-100 min-h-screen flex flex-col">
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
      <div className="flex items-center justify-between mb-2 mt-4">
        <Link
          to="/dashboard/mdm/room-management/rooms"
          className="flex items-center text-gray-400 hover:text-black"
        >
          <FaArrowLeft className="mr-3 text-md" />
          <span className="text-lg">Back to Room Management</span>
        </Link>
      </div>

      <form className="p-6 space-y-6 mb-8">
        <div className="grid grid-cols-2 gap-4">
          <div className="flex flex-col space-y-2">
            <label className="font-semibold">Room Type Name</label>
            <select
              value={roomType}
              onChange={(e) => setRoomType(e.target.value)}
              className="border border-gray-300 rounded-lg p-2.5"
              required
              disabled={isFetching}
            >
              <option value="" disabled>
                {isFetching ? 'Loading room types...' : 'Select a room type'}
              </option>
              {roomTypes.map((room) => (
                <option key={room.roomtypeid} value={room.roomtypename}>
                  {room.roomtypename}
                </option>
              ))}
            </select>
          </div>

          <div className="flex flex-col space-y-2">
            <label className="font-semibold">Base Price</label>
            <input
              type="number"
              placeholder="Base Price"
              value={basePrice}
              onChange={(e) => setBasePrice(e.target.value)}
              className="border border-gray-300 rounded-lg p-2"
              required
              disabled={isUpdating}
            />
          </div>

          <div className="col-span-2 flex flex-col space-y-2">
            <label className="font-semibold">Description</label>
            <textarea
              placeholder="Enter room description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="border border-gray-300 rounded-lg p-2 h-24"
              required
              disabled={isUpdating}
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="flex flex-col space-y-2">
            <label className="font-semibold">Max Occupancy</label>
            <input
              type="number"
              placeholder="Max Occupancy"
              value={maxOccupancy}
              onChange={(e) => setMaxOccupancy(e.target.value)}
              className="border border-gray-300 rounded-lg p-2"
              required
              disabled={isUpdating}
            />
          </div>

          <div className="flex flex-col space-y-2">
            <label className="font-semibold">Deposit Amount</label>
            <input
              type="number"
              placeholder="Deposit Amount"
              value={depositAmount}
              onChange={(e) => setDepositAmount(e.target.value)}
              className="border border-gray-300 rounded-lg p-2"
              required
              disabled={isUpdating}
            />
          </div>
        </div>

        {/* Add Is Refundable Toggle Field */}
        <div className="grid grid-cols-2 gap-4">
          <div className="flex flex-col space-y-2">
            <label className="font-semibold">Reservation Terms</label>
            <div className="flex items-center justify-between pt-2">
              {/* Text on the left side */}
              <div>
                <span className="font-medium text-gray-700">
                  {isRefundable ? 'Refundable' : 'Non-refundable'}
                </span>
                <p className="text-sm text-gray-500 mt-1">
                  {isRefundable
                    ? 'Reservation for this room type are refundable'
                    : 'Reservation for this room type are non-refundable'}
                </p>
              </div>

              {/* Toggle on the right side */}
              <button
                type="button"
                onClick={toggleRefundable}
                disabled={isUpdating}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-300 focus:outline-none ${
                  isRefundable ? 'bg-black' : 'bg-gray-200'
                } ${isUpdating ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                {/* Toggle knob */}
                <span
                  className={`inline-block h-4 w-4 transform rounded-full transition-transform duration-300 ${
                    isRefundable ? 'translate-x-6 bg-white' : 'translate-x-1 bg-gray-100'
                  }`}
                />
              </button>
            </div>
          </div>
        </div>

        <div className="space-y-2">
          <label className="font-semibold">Upload Room Images</label>
          <div
            {...getRootProps()}
            className={`flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-blue-400 rounded-lg cursor-pointer hover:bg-blue-50 ${
              isUpdating ? 'opacity-50 cursor-not-allowed' : ''
            }`}
          >
            <input {...getInputProps()} disabled={isUpdating} />
            <p className="text-gray-500">Drag & drop images here, or click to select files</p>
          </div>
          <div className="mt-2 flex flex-wrap gap-2">
            {images.map((file, index) => (
              <div key={index} className="relative">
                <img
                  src={file.preview}
                  alt="preview"
                  className="w-16 h-16 object-cover rounded-lg"
                  onError={(e) => {
                    e.target.src = '/placeholder-image.jpg'; // Add a placeholder image
                  }}
                />
                {!isUpdating && (
                  <button
                    type="button"
                    onClick={() => removeImage(index)}
                    className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs hover:bg-red-600 hover:scale-110 transition-transform"
                    title="Remove image"
                  >
                    <FaTimes size={14} />
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-6 gap-6">
          <div className="col-span-6">
            <h3 className="text-lg font-semibold mb-1">Select Amenities</h3>
            <div className="flex flex-wrap gap-4">
              {uniqueAmenities.map((amenity) => (
                <div key={amenity.id} className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    className="h-4 w-4 mr-1"
                    checked={selectedAmenities.includes(amenity.id)}
                    onChange={() =>
                      handleCheckboxChange(selectedAmenities, setSelectedAmenities, amenity.id)
                    }
                    disabled={isUpdating}
                  />
                  <label>{amenity.name}</label>
                </div>
              ))}
            </div>
          </div>

          <div className="col-span-6">
            <h3 className="text-lg font-semibold mb-1">Select Service Categories</h3>
            <div className="flex flex-wrap gap-4">
              {uniqueServiceCategories.map((service) => (
                <div key={service.id} className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    className="h-4 w-4 mr-1"
                    checked={selectedServiceCategories.includes(service.id)}
                    onChange={() =>
                      handleCheckboxChange(
                        selectedServiceCategories,
                        setSelectedServiceCategories,
                        service.id
                      )
                    }
                    disabled={isUpdating}
                  />
                  <label>{service.name}</label>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-4 mt-4">
          <button
            type="button"
            onClick={(e) => handleSubmit(e, 'update')}
            disabled={isUpdating || !roomTypeId}
            className={`px-12 py-2 bg-black text-white rounded-lg ${
              isUpdating || !roomTypeId ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-800'
            }`}
          >
            {isUpdating ? (
              <span className="flex items-center">
                <svg
                  className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
                Updating...
              </span>
            ) : (
              'Update'
            )}
          </button>

          <button
            type="button"
            onClick={(e) => handleSubmit(e, 'delete')}
            disabled={isDeleting || !roomTypeId}
            className={`px-16 py-2 bg-red-500 text-white rounded-lg text-md font-semibold ${
              isDeleting || !roomTypeId ? 'opacity-50 cursor-not-allowed' : 'hover:bg-red-600'
            }`}
          >
            {isDeleting ? 'Deleting...' : 'Delete'}
          </button>
        </div>
      </form>

      {isDeleteModalOpen && (
        <div className="fixed inset-0 flex justify-center items-center bg-black bg-opacity-50">
          <div className="bg-white p-6 rounded-lg w-96">
            <h3 className="text-lg font-semibold mb-4">
              Are you sure you want to delete the room type: <strong>{roomType}</strong>?
            </h3>
            <div className="flex justify-end gap-4">
              <button
                onClick={() => setIsDeleteModalOpen(false)}
                disabled={isDeleting}
                className="px-6 py-2 bg-gray-500 text-white hover:bg-gray-600 rounded-lg disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={deleteRoomType}
                disabled={isDeleting}
                className="px-6 py-2 bg-red-500 text-white hover:bg-red-600 rounded-lg disabled:opacity-50"
              >
                {isDeleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Loading overlay for initial fetch */}
      {isFetching && (
        <div className="fixed inset-0 flex justify-center items-center bg-black bg-opacity-30 z-50">
          <div className="bg-white p-4 rounded-lg shadow-lg flex items-center space-x-3">
            <svg
              className="animate-spin h-6 w-6 text-black"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              ></circle>
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              ></path>
            </svg>
            <span className="text-lg font-medium">Loading room types...</span>
          </div>
        </div>
      )}
    </div>
  );
}

export default ExistingRoom;
